/**
 * Colorimetry: transfer functions (EOTFs), RGB primaries, RGB->XYZ matrices,
 * chromaticity coordinates, CIE L* and correlated colour temperature.
 */
import type { PrimariesId, TransferId } from './types';

export type Mat3 = [number, number, number, number, number, number, number, number, number];

export interface Chromaticities {
  r: [number, number];
  g: [number, number];
  b: [number, number];
  w: [number, number];
}

export const D65: [number, number] = [0.3127, 0.329];
export const D50: [number, number] = [0.3457, 0.3585];

export const PRIMARIES: Record<Exclude<PrimariesId, 'custom'>, { label: string; c: Chromaticities }> = {
  srgb: { label: 'sRGB / BT.709', c: { r: [0.64, 0.33], g: [0.3, 0.6], b: [0.15, 0.06], w: D65 } },
  p3: { label: 'Display P3', c: { r: [0.68, 0.32], g: [0.265, 0.69], b: [0.15, 0.06], w: D65 } },
  bt2020: { label: 'BT.2020 / BT.2100', c: { r: [0.708, 0.292], g: [0.17, 0.797], b: [0.131, 0.046], w: D65 } },
  adobe: { label: 'Adobe RGB (1998)', c: { r: [0.64, 0.33], g: [0.21, 0.71], b: [0.15, 0.06], w: D65 } },
  prophoto: { label: 'ProPhoto RGB (D50)', c: { r: [0.7347, 0.2653], g: [0.1596, 0.8404], b: [0.0366, 0.0001], w: D50 } },
};

export const TRANSFER_LABELS: Record<TransferId, string> = {
  srgb: 'sRGB (IEC 61966-2-1)',
  gamma: 'Pure power law (gamma)',
  bt709: 'BT.709 camera curve (inverse OETF)',
  bt1886: 'BT.1886 (with black level)',
  pq: 'PQ / SMPTE ST 2084 (HDR)',
  hlg: 'HLG / BT.2100 (HDR)',
  linear: 'Linear (no conversion)',
  curve: 'Tone curve from embedded ICC profile',
};

// ---------------------------------------------------------------- matrices

export function mat3Mul(a: Mat3, b: Mat3): Mat3 {
  const r = new Array(9).fill(0) as Mat3;
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++) r[i * 3 + j] = a[i * 3] * b[j] + a[i * 3 + 1] * b[3 + j] + a[i * 3 + 2] * b[6 + j];
  return r;
}

export function mat3Vec(m: Mat3, v: [number, number, number]): [number, number, number] {
  return [m[0] * v[0] + m[1] * v[1] + m[2] * v[2], m[3] * v[0] + m[4] * v[1] + m[5] * v[2], m[6] * v[0] + m[7] * v[1] + m[8] * v[2]];
}

export function mat3Inv(m: Mat3): Mat3 {
  const [a, b, c, d, e, f, g, h, i] = m;
  const A = e * i - f * h;
  const B = -(d * i - f * g);
  const C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-15) throw new Error('Singular matrix');
  const k = 1 / det;
  return [
    A * k,
    -(b * i - c * h) * k,
    (b * f - c * e) * k,
    B * k,
    (a * i - c * g) * k,
    -(a * f - c * d) * k,
    C * k,
    -(a * h - b * g) * k,
    (a * e - b * d) * k,
  ];
}

function xyToXYZ(x: number, y: number): [number, number, number] {
  return [x / y, 1, (1 - x - y) / y];
}

/** Standard derivation of the RGB->XYZ matrix from primaries and white point (Y_white = 1). */
export function rgbToXyzMatrix(c: Chromaticities): Mat3 {
  const R = xyToXYZ(...c.r);
  const G = xyToXYZ(...c.g);
  const B = xyToXYZ(...c.b);
  const P: Mat3 = [R[0], G[0], B[0], R[1], G[1], B[1], R[2], G[2], B[2]];
  const S = mat3Vec(mat3Inv(P), xyToXYZ(...c.w));
  return [P[0] * S[0], P[1] * S[1], P[2] * S[2], P[3] * S[0], P[4] * S[1], P[5] * S[2], P[6] * S[0], P[7] * S[1], P[8] * S[2]];
}

/** Bradford chromatic adaptation matrix from white `src` to white `dst` (xy). */
export function bradford(src: [number, number], dst: [number, number]): Mat3 {
  const M: Mat3 = [0.8951, 0.2664, -0.1614, -0.7502, 1.7135, 0.0367, 0.0389, -0.0685, 1.0296];
  const s = mat3Vec(M, xyToXYZ(...src));
  const d = mat3Vec(M, xyToXYZ(...dst));
  const D: Mat3 = [d[0] / s[0], 0, 0, 0, d[1] / s[1], 0, 0, 0, d[2] / s[2]];
  return mat3Mul(mat3Inv(M), mat3Mul(D, M));
}

/** Matrix converting linear RGB in primaries `from` to linear sRGB (for display only). */
export function toLinearSrgbMatrix(fromRgbToXyz: Mat3, fromWhite: [number, number]): Mat3 {
  const xyzToSrgb = mat3Inv(rgbToXyzMatrix(PRIMARIES.srgb.c));
  const adapt = bradford(fromWhite, D65);
  return mat3Mul(xyzToSrgb, mat3Mul(adapt, fromRgbToXyz));
}

// ---------------------------------------------------------------- transfer functions

export interface TransferParams {
  gamma: number;
  /** BT.1886 black-to-white luminance ratio Lb/Lw. */
  bt1886Black: number;
  /** HLG nominal peak display luminance in cd/m^2 (for the OOTF). */
  hlgPeak: number;
  /** If false, HLG is decoded to scene-linear light only (no OOTF). */
  hlgOotf: boolean;
  /** Uniformly sampled tone curve for transfer 'curve' (from an ICC profile). */
  curve?: number[];
}

export const DEFAULT_TRANSFER_PARAMS: TransferParams = { gamma: 2.2, bt1886Black: 0, hlgPeak: 1000, hlgOotf: true };

export function srgbEotf(v: number): number {
  const a = Math.abs(v);
  const l = a <= 0.04045 ? a / 12.92 : Math.pow((a + 0.055) / 1.055, 2.4);
  return v < 0 ? -l : l;
}

export function srgbOetf(l: number): number {
  const a = Math.abs(l);
  const v = a <= 0.0031308 ? a * 12.92 : 1.055 * Math.pow(a, 1 / 2.4) - 0.055;
  return l < 0 ? -v : v;
}

/**
 * BT.709 OETF with the exact continuity constants (the solution of the
 * 0.45 power / 4.5 toe-slope curve that dcraw/LibRaw's gamma_curve() computes;
 * BT.709 rounds them to 1.099 and 0.018).
 */
export const BT709_ALPHA = 1.09929682680944;
export const BT709_BETA = 0.018053968510807;

export function bt709Oetf(l: number): number {
  return l < BT709_BETA ? 4.5 * l : BT709_ALPHA * Math.pow(l, 0.45) - (BT709_ALPHA - 1);
}

/** Encoded [0,1] -> scene-linear [0,1]. */
export function bt709InverseOetf(v: number): number {
  return v < 4.5 * BT709_BETA ? v / 4.5 : Math.pow((v + BT709_ALPHA - 1) / BT709_ALPHA, 1 / 0.45);
}

const PQ_M1 = 2610 / 16384;
const PQ_M2 = (2523 / 4096) * 128;
const PQ_C1 = 3424 / 4096;
const PQ_C2 = (2413 / 4096) * 32;
const PQ_C3 = (2392 / 4096) * 32;

/** PQ EOTF: encoded [0,1] -> absolute luminance in cd/m^2 (0..10000). */
export function pqEotf(v: number): number {
  const p = Math.pow(Math.max(v, 0), 1 / PQ_M2);
  return 10000 * Math.pow(Math.max(p - PQ_C1, 0) / (PQ_C2 - PQ_C3 * p), 1 / PQ_M1);
}

export function pqInverseEotf(nits: number): number {
  const y = Math.pow(Math.max(nits, 0) / 10000, PQ_M1);
  return Math.pow((PQ_C1 + PQ_C2 * y) / (1 + PQ_C3 * y), PQ_M2);
}

const HLG_A = 0.17883277;
const HLG_B = 1 - 4 * HLG_A;
const HLG_C = 0.5 - HLG_A * Math.log(4 * HLG_A);

/** HLG inverse OETF: encoded [0,1] -> normalised scene-linear [0,1]. */
export function hlgInverseOetf(v: number): number {
  return v <= 0.5 ? (v * v) / 3 : (Math.exp((v - HLG_C) / HLG_A) + HLG_B) / 12;
}

export function hlgOetf(e: number): number {
  return e <= 1 / 12 ? Math.sqrt(3 * e) : HLG_A * Math.log(12 * e - HLG_B) + HLG_C;
}

export function hlgSystemGamma(peak: number): number {
  return 1.2 + 0.42 * Math.log10(peak / 1000);
}

export function bt1886Eotf(v: number, blackRatio: number): number {
  const lw = 1;
  const lb = Math.max(0, blackRatio);
  const g = 2.4;
  const d = Math.pow(lw, 1 / g) - Math.pow(lb, 1 / g);
  const a = Math.pow(d, g);
  const b = Math.pow(lb, 1 / g) / d;
  return a * Math.pow(Math.max(v + b, 0), g);
}

/**
 * Per-channel transfer function (encoded -> linear). For PQ the output is in
 * cd/m^2. For HLG the output is scene-linear; the OOTF (which mixes channels)
 * is applied separately by the pipeline.
 */
export function makeEotf(t: TransferId, p: TransferParams): (v: number) => number {
  switch (t) {
    case 'srgb':
      return srgbEotf;
    case 'gamma': {
      const g = p.gamma;
      return (v) => (v < 0 ? -Math.pow(-v, g) : Math.pow(v, g));
    }
    case 'bt709':
      return bt709InverseOetf;
    case 'bt1886': {
      const k = p.bt1886Black;
      return (v) => bt1886Eotf(v, k);
    }
    case 'pq':
      return pqEotf;
    case 'hlg':
      return hlgInverseOetf;
    case 'linear':
      return (v) => v;
    case 'curve': {
      const c = p.curve;
      if (!c || c.length < 2) return (v) => v;
      const n = c.length - 1;
      return (v) => {
        const x = Math.min(Math.max(v, 0), 1) * n;
        const i = Math.min(n - 1, Math.floor(x));
        return c[i] + (x - i) * (c[i + 1] - c[i]);
      };
    }
  }
}

/** True when the transfer function yields absolute luminance (cd/m^2). */
export function transferIsAbsolute(t: TransferId, p: TransferParams): boolean {
  return t === 'pq' || (t === 'hlg' && p.hlgOotf);
}

// ---------------------------------------------------------------- CIE quantities

export function lstar(Y: number, Yn: number): number {
  const t = Y / Yn;
  const d = 6 / 29;
  const f = t > d * d * d ? Math.cbrt(t) : t / (3 * d * d) + 4 / 29;
  return 116 * f - 16;
}

export function xyzToXy(X: number, Y: number, Z: number): [number, number] {
  const s = X + Y + Z;
  if (!(s > 0)) return [NaN, NaN];
  return [X / s, Y / s];
}

/** CIE 1976 UCS u', v'. */
export function xyzToUpVp(X: number, Y: number, Z: number): [number, number] {
  const d = X + 15 * Y + 3 * Z;
  if (!(d > 0)) return [NaN, NaN];
  return [(4 * X) / d, (9 * Y) / d];
}

/** Planckian locus approximation (Kim et al. 2002), valid 1667 K..25000 K. */
export function planckianXy(T: number): [number, number] {
  const t = T;
  const x =
    t <= 4000
      ? -0.2661239e9 / (t * t * t) - 0.2343589e6 / (t * t) + 0.8776956e3 / t + 0.17991
      : -3.0258469e9 / (t * t * t) + 2.1070379e6 / (t * t) + 0.2226347e3 / t + 0.24039;
  let y: number;
  if (t <= 2222) y = -1.1063814 * x ** 3 - 1.3481102 * x ** 2 + 2.18555832 * x - 0.20219683;
  else if (t <= 4000) y = -0.9549476 * x ** 3 - 1.37418593 * x ** 2 + 2.09137015 * x - 0.16748867;
  else y = 3.081758 * x ** 3 - 5.8733867 * x ** 2 + 3.75112997 * x - 0.37001483;
  return [x, y];
}

function xyToUv1960(x: number, y: number): [number, number] {
  const d = -2 * x + 12 * y + 3;
  return [(4 * x) / d, (6 * y) / d];
}

export interface CctResult {
  cct: number;
  duv: number;
  /** False when |Duv| > 0.05 or the result lies outside 1667..25000 K. */
  valid: boolean;
}

/**
 * Isotemperature table (Robertson's method) generated from the Planckian
 * locus approximation: for each reciprocal temperature (mired) the locus
 * point in CIE 1960 (u, v) and its unit tangent.
 */
const MIRED_MIN = 40;
const MIRED_MAX = 600;
const MIRED_STEP = 0.25;
let robertson: { u: Float64Array; v: Float64Array; tu: Float64Array; tv: Float64Array } | null = null;

function robertsonTable() {
  if (robertson) return robertson;
  const n = Math.round((MIRED_MAX - MIRED_MIN) / MIRED_STEP) + 1;
  const u = new Float64Array(n);
  const v = new Float64Array(n);
  const tu = new Float64Array(n);
  const tv = new Float64Array(n);
  const uvAt = (m: number) => xyToUv1960(...planckianXy(1e6 / m));
  for (let i = 0; i < n; i++) {
    const m = MIRED_MIN + i * MIRED_STEP;
    [u[i], v[i]] = uvAt(m);
    const a = uvAt(m - 0.01);
    const b = uvAt(m + 0.01);
    const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
    tu[i] = (b[0] - a[0]) / l;
    tv[i] = (b[1] - a[1]) / l;
  }
  robertson = { u, v, tu, tv };
  return robertson;
}

/**
 * Correlated colour temperature: the locus point whose normal passes through
 * (u, v) in CIE 1960 UCS (Robertson's method, binary search over a 0.25 mired
 * table with linear interpolation), and signed Duv (positive above the locus).
 */
export function cctFromXy(x: number, y: number): CctResult {
  if (!Number.isFinite(x) || !Number.isFinite(y) || x + y <= 0) return { cct: NaN, duv: NaN, valid: false };
  const [u, v] = xyToUv1960(x, y);
  const t = robertsonTable();
  const n = t.u.length;
  // d(i): projection of (u,v) - locus(i) on the tangent; zero at the foot of the perpendicular.
  const d = (i: number) => (u - t.u[i]) * t.tu[i] + (v - t.v[i]) * t.tv[i];
  let lo = 0;
  let hi = n - 1;
  const dlo = d(lo);
  const dhi = d(hi);
  if (dlo < 0 || dhi > 0) return { cct: NaN, duv: NaN, valid: false };
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (d(mid) > 0) lo = mid;
    else hi = mid;
  }
  const a = d(lo);
  const b = d(hi);
  const f = a - b !== 0 ? a / (a - b) : 0;
  const mired = MIRED_MIN + (lo + f) * MIRED_STEP;
  const T = 1e6 / mired;
  const [pu, pv] = xyToUv1960(...planckianXy(T));
  const duv = Math.sign(v - pv) * Math.hypot(u - pu, v - pv);
  const valid = Math.abs(duv) <= 0.05 && T >= 1667 && T <= 25000;
  return { cct: T, duv, valid };
}
